

import React from 'react'

/**
 * Utility function to determine which pages and ellipses should be displayed.
 * It ensures the first, last, current page, and a few surrounding pages are visible.
 * This keeps the pagination bar manageable.
 * @param {number} totalPages - The total number of pages available.
 * @param {number} currentPage - The currently active page number.
 * @returns {Array<number|string>} An array of page numbers or '...' for ellipsis.
 */
const getPageNumbers = (totalPages, currentPage) => {
    // Determine the maximum number of visible page links (excluding first/last/arrows)
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
        // If total pages is small, just show all pages
        for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
        }
        return pages;
    }

    // Always include the first page
    pages.push(1);

    // Calculate the pages around the current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Adjust start/end to ensure maxVisiblePages links are shown if possible
    if (currentPage <= 3) {
        endPage = 4;
    } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
    }

    // Add left ellipsis
    if (startPage > 2) {
        pages.push('...');
    }

    // Add pages in the middle
    for (let i = startPage; i <= endPage; i++) {
        // Only push if the page number is valid and not already 1 or totalPages
        if (i > 1 && i < totalPages) {
            pages.push(i);
        }
    }

    // Add right ellipsis
    if (endPage < totalPages - 1) {
        pages.push('...');
    }
    
    // Always include the last page, unless totalPages is 1 and already pushed
    if (totalPages > 1 && pages[pages.length - 1] !== totalPages) {
        pages.push(totalPages);
    }

    // Final cleanup for potential duplicate '...' near the start/end
    return pages.filter((page, index) => !(page === '...' && pages[index - 1] === '...'));
};


function Pegination({ currentPage, totalPages, onPageChange }) {
    // Generate the array of pages to display (e.g., [1, '...', 4, 5, 6, '...', 10])
    const pagesToRender = getPageNumbers(totalPages, currentPage);

    const handlePageClick = (page, event) => {
        event.preventDefault(); // Prevent default link behavior
        // Only call onPageChange if the page is a number and not the current page
        if (typeof page === 'number' && page !== currentPage) {
            onPageChange(page);
        }
    };

    return (
        <nav aria-label="Page navigation">
            <div className="pagination-container">
                <ul className="pagination mb-0">
                    {/* 1. Previous Button */}
                    <li className="prev-next-btn"> 
                        <a 
                            className="page-link" 
                            href="#" 
                            aria-label="Previous"
                            onClick={(e) => handlePageClick(currentPage - 1, e)}
                            // Disable the link visually and functionally if on the first page
                            style={{ opacity: currentPage === 1 ? 0.5 : 1, pointerEvents: currentPage === 1 ? 'none' : 'auto' }}
                        >
                            <span aria-hidden="true">
                                <img src="/prew-icon.svg" alt="Previous" />
                            </span>
                        </a>
                    </li>

                    {/* 2. Dynamic Pagination Pill */}
                    <li className="pagination-pill"> 
                        <div className="d-flex align-items-center"> 
                            {pagesToRender.map((page, index) => {
                                if (page === '...') {
                                    // Render Ellipsis
                                    return (
                                        <span 
                                            key={`ellipsis-${index}`} 
                                            className="ellipsis" 
                                            // Ensure ellipsis aligns with other items
                                            style={{ padding: '0 5px' }} 
                                        >
                                            ...
                                        </span>
                                    );
                                }

                                // Render Page Number Link
                                const isActive = page === currentPage;
                                return (
                                    <a
                                        key={page}
                                        className={`page-link ${isActive ? 'active' : ''}`}
                                        href="#"
                                        aria-current={isActive ? 'page' : undefined}
                                        onClick={(e) => handlePageClick(page, e)}
                                    >
                                        {/* If you require the active-dot span for a specific CSS style, keep it. 
                                            Otherwise, the page number itself is enough. I've removed it for cleaner output.
                                        {isActive && <span className="active-dot"></span>} */}
                                        {page}
                                    </a>
                                );
                            })}
                        </div>
                    </li>

                    {/* 3. Next Button */}
                    <li className="prev-next-btn"> 
                        <a 
                            className="page-link" 
                            href="#" 
                            aria-label="Next"
                            onClick={(e) => handlePageClick(currentPage + 1, e)}
                            // Disable the link visually and functionally if on the last page
                            style={{ opacity: currentPage === totalPages ? 0.5 : 1, pointerEvents: currentPage === totalPages ? 'none' : 'auto' }}
                        >
                            <span aria-hidden="true">
                                <img src="/next-icon.svg" alt="Next" />
                            </span>
                        </a>
                    </li>
                </ul>
            </div>
        </nav>
    )
}

export default Pegination